import { z } from 'zod';

import { advancedConfigurationSchema } from './advanced-config.schema';
import { agentGraphShapeSchema } from './agent-graph.schema';
import { toolSchema } from './tool.schema';

/**
 * Per-configuration wording overrides for the tools the LLM sees. Keyed by
 * tool name (built-in enum value or custom_tools.name). Only the description
 * strings change; parameter shapes and handlers never do.
 */
export const toolOverrideSchema = z.object({
  description: z.string().trim().min(1).max(2000).optional(),
  /** Parameter name → replacement description. */
  parameters: z.record(z.string(), z.string().trim().min(1).max(1000)).optional(),
});
export type ToolOverride = z.infer<typeof toolOverrideSchema>;

export const toolOverridesSchema = z.record(z.string(), toolOverrideSchema);
export type ToolOverrides = z.infer<typeof toolOverridesSchema>;

/**
 * Everything that shapes how an agent behaves on a call, and nothing
 * operational (phone numbers, workflows, widget settings stay out). This is
 * what a config_commits row snapshots and what a session can be pinned to.
 */
export const agentBehaviorSnapshotSchema = z.object({
  prompt_template: z.string(),
  text_prompt_template: z.string().nullable(),
  welcome_message: z.string().nullable(),
  prompt_rules: z.string().nullable(),
  llm_model: z.string().nullable(),
  text_llm_model: z.string().nullable(),
  voice_name: z.string().nullable(),
  advanced: advancedConfigurationSchema.passthrough(),
  tools: z.array(toolSchema),
  custom_tool_ids: z.array(z.string().uuid()),
  tool_overrides: toolOverridesSchema,
  multi_agent_enabled: z.boolean(),
  agent_graph: agentGraphShapeSchema.nullable(),
  text_agent_graph: agentGraphShapeSchema.nullable(),
});
export type AgentBehaviorSnapshot = z.infer<typeof agentBehaviorSnapshotSchema>;

/** Field order is the diff/display order in the UI. */
export const AGENT_BEHAVIOR_SNAPSHOT_FIELDS = agentBehaviorSnapshotSchema.keyof().options;
